import React from "react";

import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Link,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import clsx from "clsx";

import Navigation from "../Navigation";
import useListRunningWebTrackerScans from "./queries/useListRunningWebTrackerScans";
import { RunningWebTrackerScan } from "./types";
import CreateSiteScannerForm from "./SiteScannerPage/CreateSiteScannerForm";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  timeZoneName: "short",
});

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  card: {
    width: "100%",
    minHeight: 500,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  darkText: {
    color: "#263238",
  },
  link: {
    cursor: "pointer",
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-100%) translateY(-50%)",
  },
  pageTitle: {
    paddingTop: 12,
  },
  tableBody: {
    position: "relative",
    height: 200,
  },
}));

function SiteScannerPage() {
  const classes = useStyles();

  const {
    data: runningScans,
    isLoading: runningScansLoading,
  } = useListRunningWebTrackerScans();

  const scans: RunningWebTrackerScan[] = runningScans?.scans ?? [];

  const sortedScans = scans.sort((a, b) => {
    const aTime = a.startedAt?.getTime() ?? Number.MAX_VALUE;
    const bTime = b.startedAt?.getTime() ?? Number.MAX_VALUE;
    return bTime - aTime;
  });

  return (
    <Navigation>
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Link color="inherit">
            <Typography variant="overline">Web Trackers</Typography>
          </Link>
          <Typography
            className={clsx(classes.link, classes.darkText)}
            variant="overline"
          >
            Site Scanner
          </Typography>
        </Breadcrumbs>
      </div>

      <CreateSiteScannerForm />

      <Card
        className={classes.card}
        style={{ marginTop: "1em", marginBottom: "1em" }}
      >
        <CardHeader
          className={classes.cardHeader}
          title="Running Web Tracker Monitoring Scans in AWS ECS"
        />
        <Divider />
        <CardContent className={classes.cardContent}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Starting URL</TableCell>
                  <TableCell>Last Status</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Stopped At</TableCell>
                  <TableCell>Params</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                {runningScansLoading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <CircularProgress className={classes.loader} />
                    </TableCell>
                  </TableRow>
                )}
                {sortedScans.map((scan) => {
                  return (
                    <TableRow key={scan.taskARN}>
                      <TableCell>{scan.startingURL}</TableCell>
                      <TableCell>{scan.lastStatus}</TableCell>
                      <TableCell>
                        {scan.startedAt !== null &&
                          dateFormatter.format(scan.startedAt)}
                      </TableCell>
                      <TableCell>
                        {scan.stoppedAt !== null &&
                          dateFormatter.format(scan.stoppedAt)}
                      </TableCell>
                      <TableCell>{scan.params.join(" ")}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Navigation>
  );
}

export default SiteScannerPage;
